import React, {
	Dispatch,
	RefAttributes,
	memo,
	useCallback,
	useMemo,
	useState,
} from "react";
import { Select, react } from "uikit";
import { useTranslation } from "react-i18next";

import PaymentSystem, {
	WithdrawMethod as WithdrawMethodEnum,
	PaymentProvider,
} from "../../../../../../../../services/PaymentSystem";
import useObjectEditor from "../../../../../../../../hooks/useObjectEditor";
import translationPath from "../../../../constants/translationPath";

import InternalController from "./Controller";

const WithdrawMethodBase = react.withController<
	WithdrawMethod.PropsBase,
	WithdrawMethod.Controller
>(({ value, onChange, controller, paymentProviderValue }) => {
	const [error, setError] = useState<boolean>(false);

	const { t } = useTranslation();

	const valueEditor = useObjectEditor(value, onChange);

	const withdrawMethod = valueEditor.useGetter("withdrawMethod");
	const withdrawMethodSet = valueEditor.useSetter("withdrawMethod");

	const selectOptionsWrap = useCallback(
		(item: { id: WithdrawMethodEnum; label: WithdrawMethodEnum }) => ({
			key: item.id,
			value: item.id,
			label: item.label,
		}),
		[],
	);

	const options = useMemo(
		() =>
			Object.values(WithdrawMethodEnum)
				.map((label) =>
					selectOptionsWrap({
						id: label,
						label: t([
							`${translationPath}.modal.withdraw.select.${label}`,
							label,
						]),
					}),
				)
				.filter((method) => {
					if (paymentProviderValue === PaymentProvider.MAIB) {
						return (
							method.key === WithdrawMethodEnum.WITHDRAW_WITH_HOLD
						);
					}

					return true;
				})
				.reverse(),
		[paymentProviderValue, selectOptionsWrap, t],
	);

	const updateSelectedValues = useCallback(
		(value) => {
			withdrawMethodSet(value);
			setError(false);
		},
		[withdrawMethodSet, setError],
	);

	controller.setContext({ value, setError });

	const textError = useMemo(() => {
		if (!error) return "";
		return t([
			`${translationPath}.modal.errors.withdrawMethodNotSelected`,
			"Country not selected",
		]);
	}, [t, error]);

	const placeholder = useMemo(
		() =>
			t([
				`${translationPath}.modal.placeholders.selectWithdrawMethod`,
				"Select a country",
			]),
		[t],
	);

	return (
		<>
			{paymentProviderValue === PaymentProvider.MAIB ? (
				<Select
					style={{ width: "100%" }}
					disabled={options.length === 1}
					placeholder={placeholder}
					error={textError}
					value={WithdrawMethodEnum.WITHDRAW_WITH_HOLD}
					options={options}
				/>
			) : (
				<Select
					style={{ width: "100%" }}
					disabled={options.length === 1}
					placeholder={placeholder}
					error={textError}
					value={withdrawMethod}
					options={options}
					onChange={updateSelectedValues}
				/>
			)}
		</>
	);
}, InternalController);
const WithdrawMethod = memo(WithdrawMethodBase);

declare namespace WithdrawMethod {
	type Ref = InternalController | null;
	type Controller = InternalController;
	interface PropsBase {
		value: Value;
		onChange: Dispatch<Value>;
		paymentProviderValue: PaymentProvider;
	}
	type Props = PropsBase & RefAttributes<Ref>;

	interface Value extends PaymentSystem.Model {}
}

export default WithdrawMethod;
